import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlaneTakeoff, Clock, Globe, Monitor, ChevronRight,

  RefreshCw, Search, Loader2, Radio, Zap, Filter, ListChecks,
  UserCircle2, Pause, Play, ChevronLeft, ChevronsLeft, ChevronsRight, RotateCcw,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { gateSessionsAPI } from '../../services/api';
import { gateSessionStatusKey } from '../../utils/gateSessionStatus';
import { queuePathFor } from '../../utils/queuePath';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/UI/PageHeader';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import StatusBadge from '../../components/UI/StatusBadge';
import EmptyState from '../../components/UI/EmptyState';
import { languagePairLabel } from '../../utils/languageLabel';

// 'paused' already uses yellow here, so the acknowledged tiers use blue/orange
// instead of yellow/orange to stay distinguishable on this page.
const STATUS_CONFIG = {
  pending:             { label: 'Awaiting Acknowledge', tone: 'neutral' },
  acknowledged_yellow: { label: 'Acknowledged', tone: 'info' },
  acknowledged_orange: { label: 'Acknowledged', tone: 'orange' },
  active:    { label: 'Active',    tone: 'success' },
  paused:    { label: 'Paused',    tone: 'yellow' },
  completed: { label: 'Completed', tone: 'neutral' },
  cancelled: { label: 'Cancelled', tone: 'danger' },
};

const TERMINAL_TONES = {
  T1: 'info',
  T2: 'purple',
  T3: 'orange',
};

const STATUS_TABS = ['all', 'pending', 'active', 'paused', 'completed', 'cancelled'];
const PAGE_SIZES = [25, 50, 100];

// ── Operator Cell ─────────────────────────────────────────────────────────────
// Shows who actually started/is running this session — gates are no longer
// pre-assigned to specific operators (any gate operator can pick any gate at
// session-start, CR 4.1), so this reflects the real operator, not an assignment.

function OperatorCell({ operator, small = false }) {
  const iconSize = small ? 11 : 13;
  const textCls = small ? 'text-xs text-gray-400' : 'text-xs';

  if (!operator) {
    return (
      <span className={`${textCls} text-orange-500 italic flex items-center gap-1`}>
        <UserCircle2 size={iconSize} className="text-orange-400" />
        No operator
      </span>
    );
  }

  return (
    <span className={`${textCls} text-gray-700 flex items-center gap-1`}>
      <UserCircle2 size={iconSize} className="text-gray-400 flex-shrink-0" />
      {operator.name}
    </span>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AdminGateSessionsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filters
  const [statusTab, setStatusTab] = useState('active');
  const [terminalFilter, setTerminalFilter] = useState('all');
  const [search, setSearch] = useState('');

  // Pagination — client-side over the filtered list, since every session is
  // already loaded for the tab counts and filters above.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);

  useEffect(() => {
    setPage(1);
  }, [statusTab, terminalFilter, search, pageSize]);

  // Always fetch every status — status tabs and their badge counts are both
  // derived client-side from this one list (same pattern as the terminal/search
  // filters below), so switching tabs never has to re-fetch and every badge
  // reflects a real, independent count instead of whatever the last-fetched
  // tab happened to contain.
  const loadSessions = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await gateSessionsAPI.getAll({});
      setSessions(res.data.data || []);
    } catch {
      toast.error('Failed to load gate sessions');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Emergency Pause — same confirm/warning wording as the operator session view
  // and the supervisor dashboard, so admins get the identical "emergencies only"
  // guardrail no matter which page they pause from.
  async function handlePauseResume(session, e) {
    e.stopPropagation();
    const newStatus = session.status === 'paused' ? 'active' : 'paused';
    if (newStatus === 'paused' &&
        !window.confirm('Pause all announcements for this flight? This should only be used during an operational emergency.')) {
      return;
    }
    try {
      await gateSessionsAPI.updateStatus(session.id, newStatus);
      setSessions(s => s.map(sess =>
        sess.id === session.id ? { ...sess, status: newStatus } : sess
      ));
      toast.success(newStatus === 'paused' ? 'Gate paused' : 'Gate resumed');
    } catch {
      toast.error('Failed to update gate');
    }
  }

  // Reopen — a fresh 'pending' session for a finished flight, so a gate operator
  // can enter the gate + flight again and replay the programme. The finished
  // session (and its played history) stays as it is.
  const canReopen = user?.role === 'admin';
  const [reopeningId, setReopeningId] = useState(null);

  async function handleReopen(session, e) {
    e.stopPropagation();
    if (!window.confirm(
      `Reopen flight ${session.flight_number} at gate ${session.gate?.gate_code}?\n\n` +
      'A new session will be created in "Awaiting Acknowledge". A gate operator can then enter this gate and flight again, ' +
      'Acknowledge and Initiate to play the announcements. This finished session and its history are kept.'
    )) return;
    setReopeningId(session.id);
    try {
      await gateSessionsAPI.reopen(session.id);
      toast.success(`${session.flight_number} reopened at gate ${session.gate?.gate_code}`);
      await loadSessions(true);
    } catch (err) {
      if (!err._toasted) toast.error(err.response?.data?.message || 'Failed to reopen session');
    } finally {
      setReopeningId(null);
    }
  }

  function ReopenButton({ session }) {
    if (!canReopen || !['completed', 'cancelled'].includes(session.status)) return null;
    const busy = reopeningId === session.id;
    return (
      <button
        onClick={e => handleReopen(session, e)}
        disabled={busy}
        title="Reopen — let a gate operator run this flight's announcements again"
        className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 transition-colors disabled:opacity-50"
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
        Reopen
      </button>
    );
  }

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Derived filtered list
  const filtered = sessions.filter(s => {
    if (statusTab !== 'all' && s.status !== statusTab) return false;
    const terminal = s.gate?.terminal || '';
    if (terminalFilter !== 'all' && terminal !== terminalFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        (s.flight_number || '').toLowerCase().includes(q) ||
        (s.gate?.gate_code || '').toLowerCase().includes(q) ||
        (s.destination || '').toLowerCase().includes(q) ||
        (s.airline || '').toLowerCase().includes(q) ||
        (s.operator?.name || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  // Clamp rather than reset so a refresh or pause/resume keeps the admin on the
  // same page unless the list shrank below it.
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * pageSize;
  const pageRows = filtered.slice(pageStart, pageStart + pageSize);

  // Up to 5 page numbers, centred on the current page where possible
  const firstPageNum = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
  const pageNums = Array.from({ length: Math.min(5, totalPages) }, (_, i) => firstPageNum + i);

  // Group counts for tab badges
  const counts = sessions.reduce((acc, s) => {
    acc[s.status] = (acc[s.status] || 0) + 1;
    return acc;
  }, {});
  const totalCount = sessions.length;

  // Terminal breakdown from filtered
  const terminals = [...new Set(sessions.map(s => s.gate?.terminal).filter(Boolean))].sort();

  function formatDepTime(iso) {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="px-4 py-5 max-w-7xl mx-auto">
      <PageHeader
        title="Gate Sessions"
        subtitle="All flight sessions across every gate"
        actions={
          <Button variant="ghost" size="sm" icon={RefreshCw} loading={refreshing} onClick={() => loadSessions(true)}>
            Refresh
          </Button>
        }
      />

      {/* Status tabs */}
      <div className="flex gap-1 mt-4 mb-3 overflow-x-auto pb-1">
        {STATUS_TABS.map(tab => {
          const count = tab === 'all' ? totalCount : (counts[tab] || 0);
          const active = statusTab === tab;
          return (
            <button
              key={tab}
              onClick={() => setStatusTab(tab)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                active
                  ? 'bg-blue-700 text-white'
                  : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {tab === 'all' ? 'All' : tab.charAt(0).toUpperCase() + tab.slice(1)}
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                active ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'
              }`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Filters row */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search flight, gate, destination, operator…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          />
        </div>
        <div className="flex items-center gap-1">
          <Filter size={13} className="text-gray-400" />
          <div className="flex gap-1">
            <button
              onClick={() => setTerminalFilter('all')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                terminalFilter === 'all'
                  ? 'bg-gray-800 text-white'
                  : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              All terminals
            </button>
            {terminals.map(t => (
              <button
                key={t}
                onClick={() => setTerminalFilter(terminalFilter === t ? 'all' : t)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  terminalFilter === t
                    ? 'bg-gray-800 text-white'
                    : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </div>

      <p className="text-xs text-gray-400 mb-2">
        {filtered.length === 0
          ? `Showing 0 of ${sessions.length} sessions`
          : `Showing ${pageStart + 1}–${pageStart + pageRows.length} of ${filtered.length} session${filtered.length !== 1 ? 's' : ''}`}
        {filtered.length !== sessions.length && filtered.length > 0 && ` (filtered from ${sessions.length})`}
      </p>

      {/* Table */}
      {filtered.length === 0 ? (
        <EmptyState icon={Radio} title="No sessions found" subtitle="Try adjusting the filters" />
      ) : (
        <Card padded={false} className="overflow-hidden">
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wide">
                  <th className="text-left px-4 py-3 font-semibold">Gate</th>
                  <th className="text-left px-4 py-3 font-semibold">Flight</th>
                  <th className="text-left px-4 py-3 font-semibold">Destination</th>
                  <th className="text-left px-4 py-3 font-semibold">Dep. Time</th>
                  <th className="text-left px-4 py-3 font-semibold">Language</th>
                  <th className="text-left px-4 py-3 font-semibold">Operator</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-left px-4 py-3 font-semibold">Source</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pageRows.map(session => {
                  const cfg = STATUS_CONFIG[gateSessionStatusKey(session)] || STATUS_CONFIG.completed;
                  const terminal = session.gate?.terminal || '';
                  const termTone = TERMINAL_TONES[terminal] || 'neutral';
                  return (
                    <tr
                      key={session.id}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => navigate(queuePathFor(user?.role, session.gate_id, session.id))}
                    >
                      {/* Gate */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-900 text-base">
                            {session.gate?.gate_code || '—'}
                          </span>
                          {terminal && <StatusBadge label={terminal} tone={termTone} size="sm" />}
                        </div>
                      </td>

                      {/* Flight */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <PlaneTakeoff size={13} className="text-blue-500 flex-shrink-0" />
                          <span className="font-semibold text-gray-800">{session.flight_number}</span>
                        </div>
                        {session.airline && (
                          <p className="text-xs text-gray-400 mt-0.5 ml-5">{session.airline}</p>
                        )}
                      </td>

                      {/* Destination */}
                      <td className="px-4 py-3">
                        <span className="text-gray-700">{session.destination || '—'}</span>
                        {session.destination_code && (
                          <span className="text-xs text-gray-400 ml-1">({session.destination_code})</span>
                        )}
                      </td>

                      {/* Dep time */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-gray-700">
                          <Clock size={12} className="text-gray-400" />
                          {formatDepTime(session.departure_time)}
                        </div>
                      </td>

                      {/* Language */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <Globe size={12} className="text-gray-400" />
                          {languagePairLabel(session.secondary_language)}
                        </div>
                      </td>

                      {/* Operator */}
                      <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                        <OperatorCell operator={session.operator} />
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">
                        <StatusBadge label={cfg.label} tone={cfg.tone} dot />
                      </td>

                      {/* Source */}
                      <td className="px-4 py-3">
                        {session.reopened_at ? (
                          <StatusBadge label="Reopened" tone="info" />
                        ) : session.auto_created ? (
                          <StatusBadge label="Auto" tone="purple" />
                        ) : (
                          <span className="text-xs text-gray-400">Manual</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 justify-end">
                          <ReopenButton session={session} />
                          {['active', 'paused'].includes(session.status) && (
                            <button
                              onClick={e => handlePauseResume(session, e)}
                              title={session.status === 'paused' ? 'Resume' : 'Emergency Pause — operational emergencies only'}
                              className={`p-1.5 rounded-lg transition-colors ${
                                session.status === 'paused'
                                  ? 'hover:bg-green-50 text-green-600'
                                  : 'hover:bg-amber-50 text-amber-600'
                              }`}
                            >
                              {session.status === 'paused' ? <Play size={14} /> : <Pause size={14} />}
                            </button>
                          )}
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              navigate(`/admin/gates/${session.gate_id}/queue`);
                            }}
                            title="Manage queue"
                            className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-500 transition-colors"
                          >
                            <ListChecks size={14} />
                          </button>
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              window.open(`/gate-display/${session.gate_id}`, '_blank', 'noopener,noreferrer');
                            }}
                            title="Open gate display"
                            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
                          >
                            <Monitor size={14} />
                          </button>
                          <ChevronRight size={16} className="text-gray-300" />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-gray-100">
            {pageRows.map(session => {
              const cfg = STATUS_CONFIG[gateSessionStatusKey(session)] || STATUS_CONFIG.completed;
              const terminal = session.gate?.terminal || '';
              const termTone = TERMINAL_TONES[terminal] || 'neutral';
              return (
                <div
                  key={session.id}
                  className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50"
                  onClick={() => navigate(queuePathFor(user?.role, session.gate_id, session.id))}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-bold text-gray-900">Gate {session.gate?.gate_code}</span>
                      {terminal && <StatusBadge label={terminal} tone={termTone} size="sm" />}
                      <StatusBadge label={cfg.label} tone={cfg.tone} />
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-gray-700">
                      <PlaneTakeoff size={12} className="text-blue-500" />
                      <span className="font-semibold">{session.flight_number}</span>
                      {session.destination && <span className="text-gray-400">→ {session.destination}</span>}
                    </div>
                    <div className="mt-0.5">
                      <OperatorCell operator={session.operator} small />
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {formatDepTime(session.departure_time)}
                      {session.reopened_at ? ' · Reopened' : session.auto_created && ' · Auto'}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <ReopenButton session={session} />
                    {['active', 'paused'].includes(session.status) && (
                      <button
                        onClick={e => handlePauseResume(session, e)}
                        title={session.status === 'paused' ? 'Resume' : 'Emergency Pause — operational emergencies only'}
                        className={`p-1.5 rounded-lg ${
                          session.status === 'paused'
                            ? 'hover:bg-green-50 text-green-600'
                            : 'hover:bg-amber-50 text-amber-600'
                        }`}
                      >
                        {session.status === 'paused' ? <Play size={14} /> : <Pause size={14} />}
                      </button>
                    )}
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        navigate(`/admin/gates/${session.gate_id}/queue`);
                      }}
                      className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-500"
                      title="Manage queue"
                    >
                      <ListChecks size={14} />
                    </button>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        window.open(`/gate-display/${session.gate_id}`, '_blank', 'noopener,noreferrer');
                      }}
                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
                    >
                      <Monitor size={14} />
                    </button>
                    <ChevronRight size={16} className="text-gray-300" />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span>Rows per page</span>
              <select
                value={pageSize}
                onChange={e => setPageSize(Number(e.target.value))}
                className="border border-gray-200 rounded-lg px-2 py-1 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <span className="ml-2">Page {currentPage} of {totalPages}</span>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(1)}
                  disabled={currentPage === 1}
                  title="First page"
                  className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronsLeft size={14} />
                </button>
                <button
                  onClick={() => setPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  title="Previous page"
                  className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={14} />
                </button>
                {pageNums.map(n => (
                  <button
                    key={n}
                    onClick={() => setPage(n)}
                    className={`min-w-[32px] px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                      n === currentPage
                        ? 'bg-blue-700 text-white'
                        : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {n}
                  </button>
                ))}
                <button
                  onClick={() => setPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  title="Next page"
                  className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={14} />
                </button>
                <button
                  onClick={() => setPage(totalPages)}
                  disabled={currentPage === totalPages}
                  title="Last page"
                  className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronsRight size={14} />
                </button>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
